import { c as _c } from "react/compiler-runtime";
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { ReactElement, useState } from 'react';
import { useFlags } from '@core/shared/ui/FlagsProvider';
import { ThemeProvider } from '@core/shared/ui/ThemeProvider';
import { ThemeMode, ThemeName } from '@core/shared/ui/themes';
import { BottomAppBar } from './BottomAppBar';
import { HeaderMenuPanel } from './HeaderMenuPanel';
import { LocalAppBar } from './LocalAppBar';

/**
 * Props for the Header component.
 * @interface HeaderProps
 */
interface HeaderProps {
  /** Flag to hide the top app bar. */
  hideTopAppBar?: boolean;
  /** Flag to hide the bottom app bar. */
  hideBottomAppBar?: boolean;
  /** Flag to remove the bottom app bar with the tab buttons. */
  hideTabButtons?: boolean;
  /** Flag to hide the spacer element (only needed on /watch). */
  hideSpacer?: boolean;
  /** Theme mode to apply to the header. */
  themeMode?: ThemeMode;
  showLanguageSwitcher?: boolean;
}

/**
 * Header component for the application.
 *
 * Renders a responsive header with top and bottom app bars, and a swipeable drawer
 * for navigation menu. The component adapts its appearance based on screen size,
 * scroll position, and feature flags.
 *
 * @param {HeaderProps} props - Component props.
 * @returns {ReactElement} Rendered Header component.
 */
export function Header(t0) {
  const $ = _c(30);
  const {
    hideTopAppBar,
    hideBottomAppBar,
    hideTabButtons,
    hideSpacer,
    themeMode: t1,
    showLanguageSwitcher: t2
  } = t0;
  const themeMode = t1 === undefined ? ThemeMode.light : t1;
  const showLanguageSwitcher = t2 === undefined ? false : t2;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  let t3;
  if ($[0] !== theme.breakpoints) {
    t3 = theme.breakpoints.only("xs");
    $[0] = theme.breakpoints;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isXS = useMediaQuery(t3);
  const lightTheme = themeMode === ThemeMode.light;
  const {
    strategies,
    journeys
  } = useFlags();
  const t4 = isXS ? 100 : 159;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      disableHysteresis: true,
      threshold: t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const bottomBarTrigger = useScrollTrigger(t5);
  const shouldShowBottomAppBar = (strategies || journeys) && hideTabButtons !== true;
  const shouldFade = hideBottomAppBar !== true || bottomBarTrigger;
  let t6;
  if ($[4] !== drawerOpen || $[5] !== hideSpacer || $[6] !== hideTopAppBar || $[7] !== showLanguageSwitcher) {
    t6 = !hideTopAppBar && <Box sx={{
      background: "background.default"
    }}><LocalAppBar hideSpacer={hideSpacer} onMenuClick={() => setDrawerOpen(_temp)} menuOpen={drawerOpen} showLanguageSwitcher={showLanguageSwitcher} /></Box>;
    $[4] = drawerOpen;
    $[5] = hideSpacer;
    $[6] = hideTopAppBar;
    $[7] = showLanguageSwitcher;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== bottomBarTrigger || $[10] !== hideSpacer || $[11] !== lightTheme || $[12] !== shouldFade || $[13] !== shouldShowBottomAppBar) {
    t7 = shouldShowBottomAppBar && <Box sx={{
      position: "relative"
    }}>{!hideSpacer && <Box data-testid="HeaderSpacer" sx={{
        height: 80
      }} />}<BottomAppBar lightTheme={lightTheme} bottomBarTrigger={bottomBarTrigger} shouldFade={shouldFade} /></Box>;
    $[9] = bottomBarTrigger;
    $[10] = hideSpacer;
    $[11] = lightTheme;
    $[12] = shouldFade;
    $[13] = shouldShowBottomAppBar;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7 || $[17] !== themeMode) {
    t8 = <ThemeProvider themeName={ThemeName.website} themeMode={themeMode} nested={true}>{t6}{t7}</ThemeProvider>;
    $[15] = t6;
    $[16] = t7;
    $[17] = themeMode;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t10;
  let t11;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setDrawerOpen(false);
    t10 = () => setDrawerOpen(true);
    t11 = {
      backdrop: {
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.13)"
        }
      },
      paper: {
        sx: {
          width: "100%",
          background: "transparent",
          boxShadow: "none",
          overflowX: "hidden",
          overscrollBehaviorY: "none"
        },
        onClick: () => setDrawerOpen(false)
      }
    };
    $[19] = t10;
    $[20] = t11;
    $[21] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      minHeight: "100%",
      display: "flex",
      justifyContent: "flex-end",
      position: "relative"
    };
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      minHeight: "100%",
      width: {
        xs: "100%",
        lg: 530
      }
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Container maxWidth="xxl" disableGutters={true} sx={t12}><Box sx={t13} onClick={_temp2}><HeaderMenuPanel onClose={() => setDrawerOpen(false)} /></Box></Container>;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== drawerOpen) {
    t15 = <ThemeProvider themeName={ThemeName.website} themeMode={ThemeMode.light} nested={true}><SwipeableDrawer anchor="right" open={drawerOpen} onClose={t9} onOpen={t10} slotProps={t11}>{t14}</SwipeableDrawer></ThemeProvider>;
    $[25] = drawerOpen;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t15 || $[28] !== t8) {
    t16 = <>{t8}{t15}</>;
    $[27] = t15;
    $[28] = t8;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  return t16;
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp(prev) {
  return !prev;
}